import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, LinearProgress, Typography } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import { IMAGE_ALLOWED_TYPES, IMAGE_MAX_BYTES, IMAGE_MAX_PER_PRODUCT } from '@src/common-utils/DataModels';
import {
  NewImageItem,
  ProductImageItem,
  moveImage,
  validateImageCount,
  validateImageFile,
} from '@src/components/product-management/productForm';

type ProductImagesFieldProps = {
  items: ProductImageItem[];
  onChange: (items: ProductImageItem[]) => void;
  disabled?: boolean;
  /** upload progress (0-100) by image item id, shown while saving */
  progress?: Record<string, number>;
  error?: string | null;
};

const acceptedTypes = Object.keys(IMAGE_ALLOWED_TYPES).join(',');

const newItemId = () => `new-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Add, preview, reorder and remove product images. New files are only held in the browser (object URL previews) and
 * are uploaded when the product is saved. The first image is the primary picture.
 */
export const ProductImagesField = ({ items, onChange, disabled, progress, error }: ProductImagesFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [messages, setMessages] = useState<string[]>([]);

  // release the object URLs of pending images when the field goes away
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(
    () => () => {
      itemsRef.current.forEach((item) => {
        if (item.kind === 'new') {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    },
    [],
  );

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // allow selecting the same file again

    if (files.length === 0) {
      return;
    }

    const countError = validateImageCount(items.length, files.length);
    if (countError) {
      setMessages([countError]);
      return;
    }

    const problems: string[] = [];
    const accepted: NewImageItem[] = [];
    files.forEach((file) => {
      const problem = validateImageFile(file);
      if (problem) {
        problems.push(problem);
      } else {
        accepted.push({ kind: 'new', id: newItemId(), file, previewUrl: URL.createObjectURL(file) });
      }
    });

    setMessages(problems);
    if (accepted.length > 0) {
      onChange([...items, ...accepted]);
    }
  };

  const handleRemove = (index: number) => {
    const removed = items[index];
    if (removed.kind === 'new') {
      URL.revokeObjectURL(removed.previewUrl);
    }
    setMessages([]);
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <Typography variant="subtitle1">
          Images ({items.length}/{IMAGE_MAX_PER_PRODUCT})
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || items.length >= IMAGE_MAX_PER_PRODUCT}
          sx={{ textTransform: 'none' }}
        >
          Add images
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept={acceptedTypes}
          onChange={handleFilesSelected}
          data-testid="product-image-input"
        />
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        JPEG, PNG, GIF or WebP, up to {IMAGE_MAX_BYTES / (1024 * 1024)} MB each. The first image is the main picture.
      </Typography>

      {(error || messages.length > 0) && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {[error, ...messages].filter(Boolean).map((message) => (
            <div key={message}>{message}</div>
          ))}
        </Alert>
      )}

      {items.length === 0 && (
        <Alert severity="info" variant="outlined">
          No images yet. At least one image is required.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {items.map((item, index) => (
          <Box
            key={item.id}
            sx={{ width: 116, border: '1px solid #e0e0e0', borderRadius: 1, p: 0.5, position: 'relative' }}
          >
            {index === 0 && (
              <Chip label="Primary" size="small" color="primary" sx={{ position: 'absolute', top: 4, left: 4 }} />
            )}
            <Box
              component="img"
              src={item.kind === 'new' ? item.previewUrl : item.url}
              alt={item.kind === 'new' ? item.file.name : item.key}
              sx={{ width: '100%', height: 96, objectFit: 'contain', display: 'block' }}
            />
            {progress?.[item.id] !== undefined && item.kind === 'new' && (
              <LinearProgress variant="determinate" value={progress[item.id]} sx={{ my: 0.5 }} />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <IconButton
                size="small"
                aria-label="Move image earlier"
                disabled={disabled || index === 0}
                onClick={() => onChange(moveImage(items, index, index - 1))}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Remove image"
                disabled={disabled}
                onClick={() => handleRemove(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Move image later"
                disabled={disabled || index === items.length - 1}
                onClick={() => onChange(moveImage(items, index, index + 1))}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
